import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Music, Check, Bell } from "lucide-react";
import { getPresave, trackPresave } from "@/lib/afmApi";
import { Logo } from "@/components/Logo";
import { fmtDate, fmtNum } from "@/lib/format";
import { Loading } from "@/components/common";

const STORE_COLORS = {
  spotify: "#1DB954", apple: "#FA243C", youtube: "#FF0000",
  tiktok: "#111827", amazon: "#00A8E1", deezer: "#A238FF", tidal: "#111827",
};

export default function PresaveLanding() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [saved, setSaved] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    getPresave(id)
      .then((d) => { setData(d); setCount(d.count || 0); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [id]);

  const doPresave = async (url) => {
    if (!saved) {
      try { const r = await trackPresave(id); setCount(r?.count ?? count + 1); } catch { setCount((c) => c + 1); }
      setSaved(true);
      toast.success("¡Pre-save registrado! Te avisaremos en el lanzamiento.");
    }
    if (url) window.open(url, "_blank");
  };

  if (status === "loading") return <div className="min-h-screen bg-[#F8F9FA]"><Loading /></div>;
  if (status === "error") return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8F9FA] text-center">
      <Logo imgClassName="h-12 w-auto" />
      <p className="mt-6 text-sm text-slate-500">Este pre-save no existe o ya no está disponible.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="mx-auto max-w-lg px-5 py-12">
        <div className="mb-8 flex justify-center"><Logo imgClassName="h-11 w-auto object-contain" /></div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)]">
          <div className="flex aspect-square w-full items-center justify-center bg-slate-900">
            {data.cover_url ? (
              <img src={data.cover_url} alt={data.title} className="h-full w-full object-cover" />
            ) : (
              <Music className="h-20 w-20 text-slate-600" />
            )}
          </div>
          <div className="p-7 text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{data.label}</div>
            <h1 className="mt-2 font-serif text-3xl font-extrabold tracking-tight text-black">{data.title}</h1>
            {data.release_date && <p className="mt-1 text-sm text-slate-500">Disponible el {fmtDate(data.release_date)}</p>}

            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600" data-testid="presave-count">
              <Bell className="h-3.5 w-3.5" /> {fmtNum(count)} pre-saves
            </div>

            <div className="mt-7 space-y-3" data-testid="presave-targets">
              {(data.targets || []).map((t) => (
                <button
                  key={t.key || t.store}
                  data-testid={`presave-store-${t.key || t.store}`}
                  onClick={() => doPresave(t.url)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-5 py-3.5 text-left transition-all hover:border-black hover:shadow-sm"
                >
                  <span className="flex items-center gap-3 font-semibold text-black">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: STORE_COLORS[t.key] || "#334155" }} />
                    {t.store}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Pre-save</span>
                </button>
              ))}
            </div>

            <button
              data-testid="presave-all-button"
              onClick={() => doPresave(null)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-black px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {saved ? <><Check className="h-4 w-4" /> Pre-save hecho</> : "Pre-save en todas las tiendas"}
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tecnología por SONA</p>
      </div>
    </div>
  );
}
